import React, { useEffect } from 'react';
import useFetch from 'use-http';
import moment from 'moment';
import { z } from 'zod';

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, Eye, Loader2, Share } from 'lucide-react';
import { Heading } from '@/components/ui/shared/heading';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';

import { useCartDispatch } from '@/components/context/CartContext';
import { useUserState } from '@/components/context/UserContext';

import { Label } from '@radix-ui/react-label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const emailSchema = z
  .string({ message: 'El. pašto adresas yra privalomas.' })
  .min(1, { message: 'El. pašto adresas yra privalomas.' })
  .email({ message: 'Neteisingas el. pašto adresas.' })
  .refine((val) => /^[\u0000-\u007F]+$/.test(val), {
    message: 'Naudokite tik lotyniškus simbolius el. pašto adrese.',
  });

const InstrumentalResearchDocumentsCard = ({
  research,
  setLeftToPaysera,
  component,
}) => {
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const { get: getBiotronicsViewerUrl } = useFetch(
    '/UserAccount/GetBiotronicsViewer',
  );
  const { get: getViewPaymentUrl } = useFetch(
    '/Registrations/Biotronics/Payment',
  );

  const { get: shareBiotronicsViewGet } = useFetch(
    '/UserAccount/ShareBiotronicsView',
  );

  // Shares history (GET requires researchId)
  const { get: getBiotronicsShares } = useFetch(
    '/UserAccount/BiotronicsShares',
  );

  // Cancel share (DELETE requires researchId and shareId)
  const { del: deleteBiotronicsShare } = useFetch(
    '/UserAccount/BiotronicsShare',
  );

  const dispatch = useCartDispatch();
  const userState = useUserState();

  const [showPaymentConfirm, setShowPaymentConfirm] = React.useState(false);
  const [paymentLoading, setPaymentLoading] = React.useState(false);
  const [showPaymentError, setShowPaymentError] = React.useState(false);

  const [showSharedDialog, setShowSharedDialog] = React.useState(false);
  const [shareButtonLoading, setShareButtonLoading] = React.useState(false);

  const [showEmailDialog, setShowEmailDialog] = React.useState(false);
  const [sharedEmail, setSharedEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState(null);

  // NEW: store PIN returned from backend (string)
  const [sharePin, setSharePin] = React.useState(null);

  // Shares UI state
  const [shares, setShares] = React.useState([]);
  const [shareLinkLoading, setShareLinkLoading] = React.useState(false);
  const [copiedShareId, setCopiedShareId] = React.useState(null);
  const [sharesFetchedOnce, setSharesFetchedOnce] = React.useState(false);

  // NEW: per-share "show pin" UI state
  const [revealedPinShareIds, setRevealedPinShareIds] = React.useState({});

  // active days (select must be chosen; default empty)
  const [activeDays, setActiveDays] = React.useState('');
  const [activeDaysError, setActiveDaysError] = React.useState(null);

  const base64toBlob = (base64Data, contentType = '') => {
    if (!base64Data || typeof base64Data !== 'string') {
      return null;
    }

    const normalizedBase64 = base64Data
      .replace(/^data:[^;]+;base64,/, '')
      .replace(/\s/g, '');

    let byteCharacters;
    try {
      byteCharacters = atob(normalizedBase64);
    } catch {
      return null;
    }
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const handleFileDownload = async (filename, fileType, fileGuid) => {
    await getFile(
      `?fileType=${fileType}&fileGuid=${fileGuid}&category=InstrumentalResearch&espbiEsi=${userState.espbiEsi}`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      if (!blob) {
        return;
      }

      const downloadLink = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      downloadLink.href = objectUrl;
      downloadLink.download = `${filename}.pdf`;
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 120);
    });
  };

  const shareBiotronicsView = async (daysInt) => {
    setShareButtonLoading(true);

    const query = userState.espbiEsi
      ? `?researchId=${research.id}&email=${sharedEmail}&activeDays=${daysInt}&espbiEsi=${userState.espbiEsi}`
      : `?researchId=${research.id}&email=${sharedEmail}&activeDays=${daysInt}`;

    shareBiotronicsViewGet(query)
      .then((res) => {
        const pin = res === null || res === undefined ? '' : String(res).trim();

        if (pin.length > 0) {
          setSharePin(pin);
          setShowSharedDialog(true);
        }

        setShareButtonLoading(false);
      })
      .finally(() => setShareButtonLoading(false));
  };

  const handleConfirmEmail = async () => {
    const result = emailSchema.safeParse(sharedEmail?.trim());
    if (!result.success) {
      setEmailError(result.error.errors[0].message);
      return;
    }
    setEmailError(null);

    if (!activeDays) {
      setActiveDaysError('Pasirinkite nuorodos galiojimą.');
      return;
    }

    const daysInt = parseInt(activeDays, 10);
    if (!Number.isFinite(daysInt) || ![7, 14, 30].includes(daysInt)) {
      setActiveDaysError('Pasirinkite nuorodos galiojimą.');
      return;
    }
    setActiveDaysError(null);

    await shareBiotronicsView(daysInt);

    setShowEmailDialog(false);
  };

  const openBiotronicsViewer = async () => {
    if (
      research.isImagingViewPaid === false ||
      research.isImagingViewPaid === null
    ) {
      setShowPaymentConfirm(true);
      return;
    }

    const { appId, tokenEnc } = await getBiotronicsViewerUrl(
      `?studyId=${encodeURIComponent(research.imagingStudyKey)}`,
    );

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://cloudpacs-lt.affidea.com/CMStudyLoader.aspx';
    form.target = '_blank';

    const appField = document.createElement('input');
    appField.type = 'hidden';
    appField.name = 'AppId';
    appField.value = appId;

    const jwtField = document.createElement('input');
    jwtField.type = 'hidden';
    jwtField.name = 'JWT';
    jwtField.value = tokenEnc;

    form.appendChild(appField);
    form.appendChild(jwtField);

    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const handleBiotronicsPayment = async () => {
    setPaymentLoading(true);

    const query = `?component=${component}&slotId=${research.slotId}&researchId=${
      component === 'visits' ? research.slotId : research.id
    }&serviceId=${research.serviceId ?? ''}&departmentId=${
      research.departmentId ?? ''
    }`;

    const res = await getViewPaymentUrl(query);

    if (res.status >= 200) {
      setPaymentLoading(false);
      setShowPaymentError(true);
      setShowPaymentConfirm(false);
    }

    if (res.hasSucceeded) {
      if (typeof setLeftToPaysera === 'function') {
        setLeftToPaysera(true);
      }

      dispatch({
        type: 'SET_REGISTRATION_ID',
        registrationId: res.registrationId,
        orderType: 'registrationService',
      });

      window.location.href = res.paymentUrl;
      setPaymentLoading(false);
    }
  };

  const handleShareLinkFetch = async () => {
    if (!research?.id) return;

    setShareLinkLoading(true);
    try {
      const res = await getBiotronicsShares(`?researchId=${research.id}`);
      const list = Array.isArray(res) ? res : [];
      setShares(list);

      // NEW: reset revealed pins on refresh to avoid stale state
      setRevealedPinShareIds({});
    } finally {
      setShareLinkLoading(false);
      setSharesFetchedOnce(true);
    }
  };

  useEffect(() => {
    const isEligibleForShares =
      research?.id &&
      research.isPaid &&
      research.imagingStudyKey &&
      research.isImagingViewPaid;

    if (!isEligibleForShares) return;
    if (sharesFetchedOnce) return;

    handleShareLinkFetch();
  }, [
    research?.id,
    research?.isPaid,
    research?.imagingStudyKey,
    research?.isImagingViewPaid,
    userState?.role,
    sharesFetchedOnce,
  ]);

  const handleCopyEncryptedLink = async (share) => {
    if (!share?.encryptedLink) return;

    await navigator.clipboard.writeText(share.encryptedLink);

    setCopiedShareId(share.id);
    setTimeout(() => {
      setCopiedShareId(null);
    }, 5000);
  };

  const handleCancelShare = async (shareId) => {
    if (!research?.id || !shareId) return;

    await deleteBiotronicsShare(`?shareId=${shareId}`);

    await handleShareLinkFetch();
  };

  const handleRevealSharePin = (shareId) => {
    if (!shareId) return;
    setRevealedPinShareIds((prev) => ({
      ...prev,
      [shareId]: true,
    }));
  };

  return (
    <>
      {(research.observations?.[0]?.file?.guid ||
        (research.isPaid && research.imagingStudyKey)) && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">Dokumentai</Heading>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              {research.observations?.[0]?.file?.guid && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => {
                    const fileName = `Instrumentinis tyrimas ${moment(
                      research.observations[0].date,
                    ).format('YYYY-MM-DD')}`;
                    handleFileDownload(
                      fileName,
                      research.observations[0].file.type,
                      research.observations[0].file.guid,
                    );
                  }}
                >
                  <FileDown className="w-4 h-4" />
                  Atsisiųsti tyrimo PDF
                </Button>
              )}

              {research.isPaid &&
                research.imagingStudyKey &&
                research.price && (
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={openBiotronicsViewer}
                  >
                    <Eye
                      className={`w-4 h-4 ${
                        research.isImagingViewPaid
                          ? 'text-green-500'
                          : 'text-yellow-500'
                      }`}
                    />
                    Peržiūrėti tyrimo vaizdus
                  </Button>
                )}

              {research.isPaid &&
                research.imagingStudyKey &&
                research.isImagingViewPaid && (
                  <Button
                    variant="outline"
                    className="gap-2"
                    loading={shareButtonLoading}
                    onClick={() => {
                      setActiveDays('');
                      setActiveDaysError(null);
                      setEmailError(null);
                      setSharedEmail('');
                      setShowEmailDialog(true);
                    }}
                  >
                    <Share className="w-4 h-4" />
                    {shareButtonLoading && (
                      <Loader2
                        className="h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    Dalintis tyrimo vaizdais
                  </Button>
                )}
            </div>

            {shareLinkLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Kraunama dalinimosi istorija...
              </div>
            )}

            {!shareLinkLoading && shares?.length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="font-semibold uppercase text-sm">
                  Dalinimosi istorija
                </div>

                <div className="space-y-2">
                  {shares.map((s) => {
                    const idVal = s.id ?? s.Id;
                    const emailVal = s.sendToEmail ?? s.SendToEmail;
                    const validToVal = s.validTo ?? s.ValidTo;
                    const cancelDateVal =
                      s.cancelationDate ?? s.CancelationDate;
                    const encryptedLinkVal = s.encryptedLink ?? s.EncryptedLink;

                    const pinCodeValRaw = s.pinCode ?? s.PinCode;
                    const pinCodeVal =
                      pinCodeValRaw === null || pinCodeValRaw === undefined
                        ? ''
                        : String(pinCodeValRaw).trim();

                    const isCanceled = !!cancelDateVal;

                    // NEW: treat as expired if validTo exists and is before now
                    const isExpired = validToVal
                      ? moment(validToVal).isBefore(moment())
                      : false;

                    const shouldShowPinLine =
                      !isCanceled && !isExpired && pinCodeVal.length > 0;

                    const isPinRevealed = !!revealedPinShareIds[idVal];

                    return (
                      <div
                        key={idVal}
                        className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="space-y-1">
                          <div>
                            <span className="font-medium">El. paštas: </span>
                            <span>{emailVal || '-'}</span>
                          </div>

                          {!isCanceled ? (
                            <div>
                              <span className="font-medium">Galioja iki: </span>
                              <span>
                                {validToVal
                                  ? moment(validToVal).format(
                                      'YYYY-MM-DD HH:mm',
                                    )
                                  : '-'}
                              </span>
                            </div>
                          ) : (
                            <div>
                              <span className="font-medium text-red-600">
                                Atšaukta:{' '}
                              </span>
                              <span>
                                {moment(cancelDateVal).format(
                                  'YYYY-MM-DD HH:mm',
                                )}
                              </span>
                            </div>
                          )}

                          {shouldShowPinLine && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Saugos kodas:</span>

                              {!isPinRevealed ? (
                                <Button
                                  type="button"
                                  variant="link"
                                  className="h-auto p-0"
                                  onClick={() => handleRevealSharePin(idVal)}
                                >
                                  Rodyti
                                </Button>
                              ) : (
                                <span className="font-semibold">
                                  {pinCodeVal}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {!isCanceled && (
                            <>
                              <Button
                                variant="outline"
                                onClick={() => handleCancelShare(idVal)}
                              >
                                Atšaukti
                              </Button>

                              <Button
                                variant="outline"
                                onClick={() =>
                                  handleCopyEncryptedLink({
                                    id: idVal,
                                    encryptedLink: encryptedLinkVal,
                                  })
                                }
                                disabled={
                                  !encryptedLinkVal || copiedShareId === idVal
                                }
                              >
                                {copiedShareId === idVal
                                  ? 'Nukopijuota'
                                  : 'Kopijuoti'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!shareLinkLoading && sharesFetchedOnce && shares?.length === 0 && (
              <div className="text-sm text-muted-foreground pt-2">
                Dalinimosi įrašų nerasta.
              </div>
            )}
          </CardContent>

          <CardFooter />

          <Dialog
            open={showPaymentConfirm}
            onOpenChange={setShowPaymentConfirm}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Apmokėjimas už paslaugas</DialogTitle>
              </DialogHeader>
              <DialogBody>
                Jeigu neįsigijote tyrimo vaizdų skaitmeninėje laikmenoje, po
                apmokėjimo juos galite matyti mano.affidea.lt portale bei tyrimo
                vaizdų nuoroda dalintis su kitais.
                <br />
                <br />
                Tyrimo vaizdų peržiūra aktyvuojama po apmokėjimo už paslaugą.
                <br />
                Paslaugos kaina {research.price} Eur.
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentConfirm(false)}
                >
                  Atšaukti
                </Button>
                <Button
                  variant="secondary"
                  loading={paymentLoading}
                  onClick={handleBiotronicsPayment}
                >
                  {paymentLoading && (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Eiti į apmokėjimą
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showPaymentError} onOpenChange={setShowPaymentError}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Įvyko klaida</DialogTitle>
              </DialogHeader>
              <DialogBody>
                Įvyko klaida generuojant apmokėjimo informaciją. Prašome
                pabandyti vėliau.
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => setShowPaymentError(false)}
                >
                  Supratau
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* SAME dialog, only text changed + PIN shown */}
          <Dialog
            open={showSharedDialog}
            onOpenChange={(open) => {
              setShowSharedDialog(open);
              if (!open) {
                setSharedEmail(null);
                setSharePin(null);
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuoroda išsiųsta</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-3 text-sm text-foreground text-left">
                  <div>
                    Nuoroda išsiųsta <span>{sharedEmail}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span>Saugos kodas:</span>
                    <strong className="font-semibold">{sharePin}</strong>
                  </div>

                  <div>
                    Tai unikalus saugos kodas, kurį adresatas turės įvesti,
                    norėdamas peržiūrėti tyrimo vaizdus. Pasidalinkite šiuo kodu
                    Jums patogiausiu būdu.
                  </div>
                </div>
              </DialogBody>

              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowSharedDialog(false);
                    setSharedEmail(null);
                    setSharePin(null);
                    handleShareLinkFetch();
                  }}
                >
                  Grįžti
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Įveskite duomenis</DialogTitle>
              </DialogHeader>

              <DialogBody className="space-y-2">
                <Label htmlFor="email">El. paštas</Label>
                <Input
                  id="email"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="vardas@pastas.lt"
                  value={sharedEmail}
                  onChange={(e) => {
                    setSharedEmail(e.target.value);
                    setEmailError(null);
                  }}
                />

                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}

                <Select
                  value={activeDays}
                  onValueChange={(value) => {
                    setActiveDays(value);
                    setActiveDaysError(null);
                  }}
                >
                  <SelectTrigger className="h-10 w-full shadow-none">
                    <SelectValue placeholder="Nuorodos galiojimas" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="7">7 d.</SelectItem>
                    <SelectItem value="14">14 d.</SelectItem>
                    <SelectItem value="30">30 d.</SelectItem>
                  </SelectContent>
                </Select>

                {activeDaysError && (
                  <p className="text-sm text-red-500">{activeDaysError}</p>
                )}
              </DialogBody>

              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowEmailDialog(false);
                    setEmailError(null);
                    setActiveDaysError(null);
                  }}
                >
                  Atšaukti
                </Button>

                <Button onClick={handleConfirmEmail}>Siųsti nuorodą</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Card>
      )}
    </>
  );
};

export default InstrumentalResearchDocumentsCard;
